import { Component, forwardRef, Input, OnDestroy, OnInit, ChangeDetectorRef } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator
} from '@angular/forms';
import { Observable, Subject, of } from 'rxjs';
import {
  distinctUntilChanged,
  map,
  takeUntil
} from 'rxjs/operators';

import { AddressConstants } from '../../../constants/address/address.constants';
import { Country, GridConfig } from '../../../models';
import { Address, AddressFieldConfig } from './../../../models/locality/address.interface';
import { MapboxAddressService } from '../../../services/mapbox/mapbox-address.service';

/**
 * Address component dedicated to Mapbox for progressive migration
 *
 * This component is an alternative to `iad-address` that uses Mapbox instead of Google Places.
 * To be used explicitly in the templates of migrated pages.
 *
 * Usage:
 * <iad-address-mapbox
 *   formControlName="address"
 *   [config]="addressValsConfig"
 *   [grid]="addressGridConfig">
 * </iad-address-mapbox>
 */
@Component({
  selector: 'iad-address-mapbox',
  templateUrl: './address-mapbox.component.html',
  styleUrls: ['./address-mapbox.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AddressMapboxComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => AddressMapboxComponent),
      multi: true
    }
  ]
})
export class AddressMapboxComponent implements OnInit, OnDestroy, ControlValueAccessor, Validator {
  @Input() config: AddressFieldConfig;
  @Input() grid: GridConfig;

  form: FormGroup;
  onChange: (value: Address) => void;
  onTouched: () => void;
  fieldNames: Record<string, string>;

  fullAddress$: Observable<Address>;
  unsubscribe$: Subject<void>;

  get value(): Address {
    return this.form.value;
  }

  @Input('value')
  set value(value: Address) {
    this.onChange(value);
    this.onTouched();
  }

  constructor(
    private fb: FormBuilder,
    private mapboxService: MapboxAddressService,
    private ref: ChangeDetectorRef
  ) {
    this.unsubscribe$ = new Subject();
    this.onChange = () => { /* noop */ };
    this.onTouched = () => { /* noop */ };
    this.fieldNames = {};
    this.form = this.fb.group({
      address1: null,
      address2: null,
      city: null,
      country: null,
      postalCode: null,
      latitude: null,
      longitude: null,
      state: null,
      pathNumber: null,
      pathName: null
    });
  }

  ngOnInit(): void {
    this.initGridConfig();
    this.applyConfig();
    this.generateUniqueFields();
    this.initListeners();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  registerOnChange(fn: () => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  writeValue(value): void {
    if (value) {
      // Ensure address1 is always a string, not an object
      let address1Value = value.address1;
      if (address1Value && typeof address1Value === 'object') {
        // Extract string from potential object
        address1Value = address1Value.title || address1Value.address1 || null;
      }

      const formValue = {
        ...value,
        address1: address1Value || null
      };
      this.form.patchValue(formValue, { emitEvent: false });
    }
  }

  validate = (control: AbstractControl): ValidationErrors | null => {
    return this.form.invalid ? { address: 'address is invalid' } : null;
  }

  resetValues(country: Country): void {
    const values: Record<string, any> = {};
    Object.keys(this.form.value).forEach((key: string) => ((values[key] = key !== 'country' ? null : country), {})),
      this.form.reset(values);
    this.form.updateValueAndValidity();
  }

  public setDisabledState?(isDisabled: boolean): void {
    isDisabled ? this.form.disable({ emitEvent: false }) : this.form.enable({ emitEvent: false });
  }

  private generateUniqueFields(): void {
    ['address2', 'city', 'postalCode'].forEach(
      (key: string) => (this.fieldNames[key] = `${key}${new Date().getTime().toString()}`)
    );
  }

  private initGridConfig(): void {
    this.grid = (this.grid && { ...AddressConstants.DEFAULT_GRID, ...this.grid }) || AddressConstants.DEFAULT_GRID;
  }

  private applyConfig(): void {
    this.config && Object.keys(this.config).forEach((key: string) => this.form.get(key).setValidators(this.config[key]));
  }

  private initListeners() {
    // Listen to form changes
    this.fullAddress$ = this.form.valueChanges.pipe(
      distinctUntilChanged(),
      map(values => this.extractAddress1(values)),
      takeUntil(this.unsubscribe$)
    );

    this.fullAddress$.subscribe((value: Address) => {
      this.onChange({ ...value });
      this.ref.detectChanges();
    });

    // Listen to address1 field changes to extract Mapbox data
    this.form.get('address1').valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((address: any) => {
        // If it's a MapboxAutocompleteResult object (from suggestion dropdown)
        if (address && typeof address === 'object' && address.title && address.value && address.suggestion) {
          // This is a MapboxAutocompleteResult, extract only the title as address1
          this.form.patchValue(
            {
              address1: address.title
            },
            { emitEvent: false }
          );
        }
        // If it's an Address object from Mapbox (with latitude/longitude)
        else if (address && typeof address === 'object' && address.latitude && address.longitude) {
          this.form.patchValue(
            {
              address1: address.address1,
              city: address.city,
              postalCode: address.postalCode,
              country: address.country,
              state: address.state,
              latitude: address.latitude,
              longitude: address.longitude,
              pathNumber: address.pathNumber,
              pathName: address.pathName
            },
            { emitEvent: false }
          );
          this.onChange(this.form.value as Address);
        }
      });
  }

  private extractAddress1(values: Record<string, any>): Address {
    // If address1 is an object, we need to extract the string value
    if (values.address1 && typeof values.address1 === 'object') {
      let address1Value: string;

      // Case 1: MapboxAutocompleteResult object (has title, value, suggestion)
      if (values.address1.title && values.address1.value && values.address1.suggestion) {
        address1Value = values.address1.title;
      }
      // Case 2: Address object from Mapbox (has address1, latitude, longitude)
      else if (values.address1.address1) {
        address1Value = values.address1.address1;
      }
      // Case 3: Unknown object, try to stringify or use empty string
      else {
        address1Value = '';
      }

      values = {
        ...values,
        address1: address1Value
      };
    }
    return values as Address;
  }
}

